import 'react-image-lightbox/style.css';
import './styles.scss';

import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import Lightbox from 'react-image-lightbox';
import { toast as Toast } from 'react-toastify';
import { Card, CardHeader, Modal, Table } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import { hasAdminRole } from '@/api/admin-access';
import {
  evaluateCigarMatcher,
  type EvaluateCigarMatcherResponse,
  findSimilarCigars,
  type FindSimilarResponse,
  generateCigarEmbeddings,
  generateCigarFingerprints,
  getEmbeddingSuccessMessage,
  hydrateCigarCandidates,
  type HydratedIdentificationCandidate,
  type HydratedSimilarityCandidate,
  hydrateSimilarityCandidates,
  type IdentificationCandidate,
  identifyCigar,
  type IdentifyCigarResponse,
  MAX_CIGAR_MATCH_IMAGES,
} from '@/api/cigar-matching';
import MatchFlowModal, {
  type MatchFlowSource,
} from '@/components/cigars/match-flow-modal';
import UploadMatch from '@/components/cigars/match-flow-modal/upload-match';
import Breadcrumb from '@/components/common/breadcrumb.component';
import MediaCropper from '@/components/cropper';
import DebounceSearchBar from '@/components/debounce-search-bar';
import Icon from '@/components/icon';
import ModalDialog from '@/components/modal-dialog';
import Button from '@/components/overrides/button';
import StandalonePagination from '@/components/pagination/standalone';
import RowMoreOptions from '@/components/table/row-more-options';
import ToastContent from '@/components/toasts/content';
import { Constants } from '@/constants';
import { UserStore } from '@/stores';
import { uploadToS3 } from '@/utils/imageUtils';

import BulkEmbeddingJobs from './bulk-embedding-jobs';
import BulkFingerprintJobs from './bulk-fingerprint-jobs';
import {
  EmbeddingCoverageCell,
  getCoverageTestingAvailability,
  useCigarEmbeddingCoverage,
} from './embedding-coverage';

let nextMarker = null;

const PAGE_SIZE = 30;
const IMAGE_PROXY_URL = 'https://img.boxpressd-proxy.com/img';
const CROPPER_MAX_DIMENSION = 2048;

const markers = localStorage.getItem('cigar-reference-image-s3-markers')
  ? JSON.parse(localStorage.getItem('cigar-reference-image-s3-markers'))
  : {};

const getApiErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const getImageKey = (imageUrl) => {
  try {
    const { pathname } = new URL(imageUrl);
    return decodeURIComponent(
      pathname.substring(pathname.lastIndexOf('/') + 1)
    );
  } catch {
    const parts = imageUrl.split('/');
    const lastPart = parts.pop();
    const [key] = lastPart.split('?');
    return key;
  }
};

const getProxiedImageUrl = (imageUrl) =>
  `${IMAGE_PROXY_URL}?src=${encodeURIComponent(imageUrl)}&format=jpeg&q=95`;

const getCropperImageUrl = (imageUrl) =>
  `${IMAGE_PROXY_URL}?src=${encodeURIComponent(
    imageUrl
  )}&w=${CROPPER_MAX_DIMENSION}&h=${CROPPER_MAX_DIMENSION}&func=bound&format=jpeg&q=88`;

const resizeDataImageForCropper = (source) =>
  new Promise<string>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(
        1,
        CROPPER_MAX_DIMENSION /
          Math.max(image.naturalWidth, image.naturalHeight)
      );
      if (scale === 1) {
        resolve(source);
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.naturalWidth * scale);
      canvas.height = Math.round(image.naturalHeight * scale);
      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('The image could not be resized for cropping.'));
        return;
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.88));
    };
    image.onerror = () =>
      reject(new Error('The image could not be loaded for cropping.'));
    image.src = source;
  });

const getSimilarityRequestKey = (imageUrls) =>
  imageUrls.map(getImageKey).join('|');

const getMatcherTestImages = (imageUrls = []) =>
  imageUrls.slice(0, MAX_CIGAR_MATCH_IMAGES);

const formatScore = (value, digits = 3) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : '—';

const formatPercentage = (value) =>
  Number.isFinite(Number(value)) ? `${(Number(value) * 100).toFixed(1)}%` : '—';

const formatDecisionReason = (reason) => {
  if (!reason || typeof reason !== 'string') return 'Not provided';
  return reason.split('_').join(' ');
};

const getUserImageUrls = (responseData) => {
  const mediaItems = Array.isArray(responseData)
    ? responseData
    : responseData?.images || [];

  return Array.from(
    new Set(
      mediaItems
        .filter(Boolean)
        .filter(
          (media) =>
            typeof media === 'string' ||
            !media.media_type ||
            media.media_type === 'image' ||
            media.media_type.startsWith('image/')
        )
        .map((media) =>
          typeof media === 'string'
            ? media
            : media.media_url || media.url || media.image_url
        )
        .filter(Boolean)
    )
  );
};

const canvasToBlob = (canvas) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('The cropped image could not be created.'));
      },
      'image/jpeg',
      0.92
    );
  });

function CigarBandRecognition() {
  const access = UserStore.useState((state) => state.access);
  const canManageCatalogRecognition = hasAdminRole(access);
  const [cigars, setCigars] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState('training');
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedCigarId, setSelectedCigarId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [imageCount, setImageCount] = useState(0);

  const [urlToImport, setUrlToImport] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [preparingImageKey, setPreparingImageKey] = useState(null);
  const [showMediaCropper, setShowMediaCropper] = useState(false);
  const [cropSource, setCropSource] = useState(null);
  const [imageToReplace, setImageToReplace] = useState(null);
  const [stagedUrlImage, setStagedUrlImage] = useState(null);
  const [isPreparingUrlImage, setIsPreparingUrlImage] = useState(false);
  const [isSavingCroppedImage, setIsSavingCroppedImage] = useState(false);
  const [userUploadedImages, setUserUploadedImages] = useState([]);
  const [isLoadingUserImages, setIsLoadingUserImages] = useState(false);
  const [userImagesError, setUserImagesError] = useState('');
  const [userImagesReloadToken, setUserImagesReloadToken] = useState(0);

  const [embeddingCigarIds, setEmbeddingCigarIds] = useState<Set<string>>(
    new Set()
  );
  const [fingerprintCigarIds, setFingerprintCigarIds] = useState<Set<string>>(
    new Set()
  );
  const [similarityImageKeys, setSimilarityImageKeys] = useState<Set<string>>(
    new Set()
  );
  const [similarityResult, setSimilarityResult] =
    useState<FindSimilarResponse | null>(null);
  const [similarityCandidates, setSimilarityCandidates] = useState<
    HydratedSimilarityCandidate[]
  >([]);
  const [similaritySourceImage, setSimilaritySourceImage] = useState(null);
  const [showSimilarityModal, setShowSimilarityModal] = useState(false);
  const [identifyImageKeys, setIdentifyImageKeys] = useState<Set<string>>(
    new Set()
  );
  const [identifyResult, setIdentifyResult] =
    useState<IdentifyCigarResponse | null>(null);
  const [identifyCandidates, setIdentifyCandidates] = useState<
    HydratedIdentificationCandidate[]
  >([]);
  const [identifySourceImage, setIdentifySourceImage] = useState(null);
  const [showIdentifyModal, setShowIdentifyModal] = useState(false);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [matchFlowSource, setMatchFlowSource] =
    useState<MatchFlowSource | null>(null);
  const [evaluationResult, setEvaluationResult] =
    useState<EvaluateCigarMatcherResponse | null>(null);
  const [evaluationCigarIds, setEvaluationCigarIds] = useState('');
  const [evaluationMaximumImages, setEvaluationMaximumImages] = useState(25);
  const [evaluationMaximumPerCigar, setEvaluationMaximumPerCigar] = useState(5);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [coverageRefreshToken, setCoverageRefreshToken] = useState(0);
  const searchMarkers = useRef(new Map<string, string>());
  const embeddingRequests = useRef(new Set<string>());
  const fingerprintRequests = useRef(new Set<string>());
  const similarityRequests = useRef(new Set<string>());
  const identifyRequests = useRef(new Set<string>());
  const cropSaveRequest = useRef(false);
  const urlImportRequest = useRef(false);
  const imageActionRequest = useRef(false);
  const userImagesRequest = useRef(0);

  const getRecords = async () => {
    setIsLoadingRecords(true);
    try {
      const query = searchText.trim();
      const searchMarkerKey = `${query}::${currentPage}`;
      const res = await axios.get(
        `${Constants.apiPath}/actions/cigars/images/objects`,
        {
          params: {
            q: query || undefined,
            next_marker: query
              ? searchMarkers.current.get(searchMarkerKey)
              : markers[currentPage],
          },
        }
      );
      if (res.data?.folders) {
        const records = res.data.folders.map((folder, index) => ({
          id: folder,
          references: {
            training: res.data.training_data[index] || [],
            tests: res.data.test_data[index] || [],
          },
        }));
        const hydratedRecords = await Promise.all(
          records.map(async (record) => {
            try {
              const cigarResponse = await axios.get(
                `${Constants.clientPath}/cigars/${record.id}`
              );
              return {
                ...record,
                fullName: cigarResponse.data?.full_name || '',
              };
            } catch {
              return { ...record, fullName: '' };
            }
          })
        );
        setCigars(hydratedRecords);

        const filteredTotal = Number(
          res.data.total_count ?? res.data.label_count
        );
        if (Number.isFinite(filteredTotal)) setTotalCount(filteredTotal);

        if (res.data.marker) {
          nextMarker = res.data.marker;
          if (query) {
            searchMarkers.current.set(
              `${query}::${currentPage + 1}`,
              nextMarker
            );
          } else {
            markers[currentPage + 1] = nextMarker;
            localStorage.setItem(
              'cigar-reference-image-s3-markers',
              JSON.stringify(markers)
            );
          }
        }
      }
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to load recognition images"
          message="The recognition image list could not be loaded."
        />,
        { position: 'top-right' }
      );
    } finally {
      setIsLoadingRecords(false);
    }
  };

  const filteredCigars = cigars;
  const visibleCoverageIds = useMemo(
    () => filteredCigars.map((cigar) => cigar.id),
    [filteredCigars]
  );
  const {
    coverageByCigarId,
    model: coverageModel,
    version: coverageVersion,
    isLoading: isLoadingCoverage,
    isUnavailable: isCoverageUnavailable,
  } = useCigarEmbeddingCoverage(visibleCoverageIds, coverageRefreshToken);

  const updateSelectedImages = (images) => {
    setSelectedImages(images);
    setCigars((currentCigars) =>
      currentCigars.map((cigar) =>
        String(cigar.id) === String(selectedCigarId)
          ? {
              ...cigar,
              references: {
                ...cigar.references,
                [selectedType]: images,
              },
            }
          : cigar
      )
    );
  };

  const removeImageFromS3 = (url) =>
    axios.delete(
      `${Constants.apiPath}/actions/cigars/${selectedCigarId}/images`,
      {
        params: {
          key: getImageKey(url),
          type: selectedType,
        },
      }
    );

  const refreshSelectedImages = async () => {
    const query = searchText.trim();
    const res = await axios.get(
      `${Constants.apiPath}/actions/cigars/images/objects`,
      {
        params: {
          q: query || undefined,
          next_marker: query
            ? searchMarkers.current.get(`${query}::${currentPage}`)
            : markers[currentPage],
        },
      }
    );
    const cigarIndex = res.data?.folders?.findIndex(
      (folder) => String(folder) === String(selectedCigarId)
    );

    if (cigarIndex === undefined || cigarIndex < 0) {
      throw new Error('The updated cigar image list could not be found.');
    }

    const dataKey = selectedType === 'tests' ? 'test_data' : 'training_data';
    const images = res.data[dataKey]?.[cigarIndex] || [];
    updateSelectedImages(images);
    return images;
  };

  const getFreshSelectedImage = async (imageUrl) => {
    const imageKey = getImageKey(imageUrl);
    const images = await refreshSelectedImages();
    const index = images.findIndex((image) => getImageKey(image) === imageKey);

    if (index < 0) {
      throw new Error('The selected image could not be found.');
    }

    return { image: images[index], images, index };
  };

  const deleteImage = (url) => {
    removeImageFromS3(url)
      .then(() => {
        const updatedImages = selectedImages.filter((item) => item !== url);
        updateSelectedImages(updatedImages);
        if (selectedType === 'training') {
          setImageCount((current) => Math.max(0, current - 1));
        }

        ModalDialog.close();
      })
      .catch((err) => {
        console.error(err);
        Toast.error(
          <ToastContent
            title="Unable to delete image"
            message={getApiErrorMessage(err, 'The image could not be deleted.')}
          />,
          { position: 'top-right' }
        );
      });
  };

  const openCropper = async (source, replacement = null) => {
    try {
      const cropperSource = source.startsWith('data:')
        ? await resizeDataImageForCropper(source)
        : getCropperImageUrl(source);
      setCropSource(cropperSource);
      setImageToReplace(replacement);
      setShowMediaCropper(true);
    } catch (err) {
      Toast.error(
        <ToastContent
          title="Unable to prepare image"
          message={getApiErrorMessage(
            err,
            'The image could not be resized for cropping.'
          )}
        />,
        { position: 'top-right' }
      );
    }
  };

  const editImage = async (imageUrl) => {
    if (imageActionRequest.current) return;

    try {
      imageActionRequest.current = true;
      setPreparingImageKey(getImageKey(imageUrl));
      const fresh = await getFreshSelectedImage(imageUrl);
      openCropper(fresh.image, fresh.image);
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to open cropper"
          message={getApiErrorMessage(
            err,
            'A fresh image URL could not be loaded.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      imageActionRequest.current = false;
      setPreparingImageKey(null);
    }
  };

  const maximizeImage = async (imageUrl) => {
    if (imageActionRequest.current) return;

    try {
      imageActionRequest.current = true;
      setPreparingImageKey(getImageKey(imageUrl));
      const fresh = await getFreshSelectedImage(imageUrl);
      setLightboxImages(fresh.images.map(getProxiedImageUrl));
      setSelectedIndex(fresh.index);
      setShowLightbox(true);
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to preview image"
          message={getApiErrorMessage(
            err,
            'A fresh image URL could not be loaded.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      imageActionRequest.current = false;
      setPreparingImageKey(null);
    }
  };

  const closeCropper = async () => {
    if (cropSaveRequest.current) return;

    const stagedImage = stagedUrlImage;
    setShowMediaCropper(false);
    setCropSource(null);
    setImageToReplace(null);
    setStagedUrlImage(null);

    if (!stagedImage) return;

    try {
      cropSaveRequest.current = true;
      setIsSavingCroppedImage(true);
      await removeImageFromS3(stagedImage);
      if (selectedType === 'training') {
        setImageCount((current) => Math.max(0, current - 1));
      }

      try {
        await refreshSelectedImages();
      } catch (refreshError) {
        console.error(refreshError);
        setShowModal(false);
        await getRecords();
        Toast.warning(
          <ToastContent
            title="URL image discarded"
            message="The original was removed, but the gallery could not be refreshed. Reopen it to see the update."
          />,
          { position: 'top-right' }
        );
      }
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to discard URL image"
          message="The imported original could not be removed. It remains available in the gallery."
        />,
        { position: 'top-right' }
      );
    } finally {
      cropSaveRequest.current = false;
      setIsSavingCroppedImage(false);
    }
  };

  const cropImageFromUrl = async () => {
    const imageUrl = urlToImport.trim();
    if (!imageUrl || urlImportRequest.current || cropSaveRequest.current)
      return;

    try {
      const parsedUrl = new URL(imageUrl);
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
        throw new Error('Enter a public HTTP or HTTPS image URL.');
      }

      urlImportRequest.current = true;
      setIsPreparingUrlImage(true);
      const existingKeys = new Set(selectedImages.map(getImageKey));
      await axios.put(
        `${Constants.apiPath}/actions/cigars/${selectedCigarId}/images`,
        {
          image_url: imageUrl,
          type: selectedType,
        }
      );
      if (selectedType === 'training') {
        setImageCount((current) => current + 1);
      }

      const refreshedImages = await refreshSelectedImages();
      const importedImage = refreshedImages.find(
        (image) => !existingKeys.has(getImageKey(image))
      );
      if (!importedImage) {
        throw new Error('The imported image could not be opened for cropping.');
      }

      setStagedUrlImage(importedImage);
      openCropper(importedImage, importedImage);
    } catch (err) {
      Toast.error(
        <ToastContent
          title="Unable to prepare image URL"
          message={getApiErrorMessage(
            err,
            'The image could not be imported for cropping.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      urlImportRequest.current = false;
      setIsPreparingUrlImage(false);
    }
  };

  const handleImageDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => openCropper(reader.result);
    reader.onerror = () => {
      Toast.error(
        <ToastContent
          title="Unable to open image"
          message="The selected image could not be read."
        />,
        { position: 'top-right' }
      );
    };
    reader.readAsDataURL(file);
  };

  const saveCroppedImage = async (croppedCanvas) => {
    if (cropSaveRequest.current) return;

    const replacement = imageToReplace;
    let replacementDeleted = !replacement;
    let galleryRefreshed = true;

    try {
      cropSaveRequest.current = true;
      setIsSavingCroppedImage(true);
      const blob = await canvasToBlob(croppedCanvas);
      const uuid = uuidv4();
      const filename = `${selectedCigarId}-${selectedType}-${uuid}.jpg`;
      const upload = await uploadToS3(
        blob,
        'cigars/recognition',
        filename,
        uuid
      );

      await axios.put(
        `${Constants.apiPath}/actions/cigars/${selectedCigarId}/images`,
        {
          image_url: upload.data.media_url,
          type: selectedType,
        }
      );

      if (replacement) {
        try {
          await removeImageFromS3(replacement);
          replacementDeleted = true;
        } catch (err) {
          console.error(err);
          if (selectedType === 'training') {
            setImageCount((current) => current + 1);
          }
          Toast.warning(
            <ToastContent
              title="Crop saved with duplicate"
              message="The cropped image was added, but the original could not be removed."
            />,
            { position: 'top-right' }
          );
        }
      } else if (selectedType === 'training') {
        setImageCount((current) => current + 1);
      }

      setShowMediaCropper(false);
      setCropSource(null);
      setImageToReplace(null);
      setStagedUrlImage(null);
      setUrlToImport('');

      try {
        await refreshSelectedImages();
      } catch (refreshError) {
        console.error(refreshError);
        galleryRefreshed = false;
        setShowModal(false);
        await getRecords();
        Toast.warning(
          <ToastContent
            title="Image saved"
            message="The image was saved, but the gallery could not be refreshed. Reopen it to see the update."
          />,
          { position: 'top-right' }
        );
      }

      if (galleryRefreshed && (!replacement || replacementDeleted)) {
        Toast.success(
          <ToastContent
            title={replacement ? 'Image updated' : 'Image added'}
            message={
              replacement
                ? 'The cropped image replaced the original in S3.'
                : 'The cropped image was added to the image library.'
            }
          />,
          { position: 'top-right' }
        );
      }
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to save cropped image"
          message={getApiErrorMessage(
            err,
            'The cropped image could not be uploaded.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      cropSaveRequest.current = false;
      setIsSavingCroppedImage(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${Constants.apiPath}/actions/cigars/images/stats`, {
        params: {
          next_marker: markers[currentPage],
        },
      })
      .then((res) => {
        setTotalCount(res.data.label_count);
        setImageCount(res.data.image_count);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  useEffect(() => {
    getRecords();
  }, [currentPage, searchText]);

  useEffect(() => {
    if (!showModal || !selectedCigarId) {
      userImagesRequest.current += 1;
      setUserUploadedImages([]);
      setIsLoadingUserImages(false);
      setUserImagesError('');
      return;
    }

    const requestId = userImagesRequest.current + 1;
    userImagesRequest.current = requestId;
    setUserUploadedImages([]);
    setIsLoadingUserImages(true);
    setUserImagesError('');

    axios
      .get(`${Constants.clientPath}/cigars/${selectedCigarId}/images`)
      .then((res) => {
        if (userImagesRequest.current !== requestId) return;
        setUserUploadedImages(getUserImageUrls(res.data));
      })
      .catch((err) => {
        if (userImagesRequest.current !== requestId) return;
        console.error(err);
        setUserImagesError(
          getApiErrorMessage(
            err,
            'The user-uploaded images could not be loaded.'
          )
        );
      })
      .finally(() => {
        if (userImagesRequest.current === requestId) {
          setIsLoadingUserImages(false);
        }
      });

    return () => {
      if (userImagesRequest.current === requestId) {
        userImagesRequest.current += 1;
      }
    };
  }, [selectedCigarId, selectedType, showModal, userImagesReloadToken]);

  const renderImageGrid = (cigarId, images, type) => {
    return (
      <button
        type="button"
        className="recognition-image-grid"
        onClick={() => {
          setSelectedType(type);
          setSelectedImages(images);
          setShowModal(true);
          setSelectedCigarId(cigarId);
        }}
      >
        {images?.slice(0, 5).map((image) => (
          <span className="recognition-thumbnail" key={image}>
            <img src={image} alt="Cigar" />
          </span>
        ))}

        <span className="recognition-image-count">
          {images?.length || 0} {images?.length === 1 ? 'image' : 'images'}
          <Icon name={images?.length ? 'chevron-right' : 'plus'} size={14} />
        </span>
      </button>
    );
  };

  const searchGoogle = (cigarId) => {
    axios
      .get(`${Constants.clientPath}/cigars/${cigarId}`)
      .then((res) => {
        if (res.data?.full_name) {
          window.open(
            `https://www.google.com/search?tbm=isch&q=${res.data.full_name}`,
            '_blank'
          );
        }
      })
      .catch((e) => {
        console.log(e.message);
        alert('Unable to get cigar details');
      });
  };

  const setEmbeddingPending = (cigarId, pending) => {
    setEmbeddingCigarIds((current) => {
      const next = new Set(current);
      if (pending) next.add(String(cigarId));
      else next.delete(String(cigarId));
      return next;
    });
  };

  const generateEmbeddings = async (cigar, force = false) => {
    const cigarId = String(cigar.id);
    if (embeddingRequests.current.has(cigarId)) return;
    try {
      embeddingRequests.current.add(cigarId);
      setEmbeddingPending(cigar.id, true);
      const result = await generateCigarEmbeddings(cigar.id, {
        maximumImages: 10,
        ...(force ? { force: true } : {}),
      });
      Toast.success(
        <ToastContent
          title="Embeddings"
          message={getEmbeddingSuccessMessage(result)}
        />,
        { position: 'top-right' }
      );
      setCoverageRefreshToken((current) => current + 1);
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to generate embeddings"
          message={getApiErrorMessage(
            err,
            'There was a problem generating the reference embeddings.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      embeddingRequests.current.delete(cigarId);
      setEmbeddingPending(cigar.id, false);
    }
  };

  const generateFingerprint = async (cigar, force = false) => {
    const cigarId = String(cigar.id);
    if (fingerprintRequests.current.has(cigarId)) return;
    try {
      fingerprintRequests.current.add(cigarId);
      setFingerprintCigarIds((current) => new Set(current).add(cigarId));
      const result = await generateCigarFingerprints(cigar.id, {
        force,
        maximumImages: 3,
      });
      Toast.success(
        <ToastContent
          title="Fingerprint generated"
          message={
            result.message ||
            `Generated ${result.generated_count.toLocaleString()} fingerprints; skipped ${result.skipped_count.toLocaleString()}.`
          }
        />,
        { position: 'top-right' }
      );
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to generate fingerprint"
          message={getApiErrorMessage(
            err,
            'There was a problem generating the cigar fingerprint.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      fingerprintRequests.current.delete(cigarId);
      setFingerprintCigarIds((current) => {
        const next = new Set(current);
        next.delete(cigarId);
        return next;
      });
    }
  };

  const setSimilarityPending = (requestKey, pending) => {
    setSimilarityImageKeys((current) => {
      const next = new Set(current);
      if (pending) next.add(requestKey);
      else next.delete(requestKey);
      return next;
    });
  };

  const runSimilaritySearch = async ({
    requestKey,
    resolveImageUrls,
    showSourceImage = false,
  }) => {
    if (!requestKey || similarityRequests.current.has(requestKey)) return;

    try {
      similarityRequests.current.add(requestKey);
      setSimilarityPending(requestKey, true);
      const imageUrls = await resolveImageUrls();
      if (!imageUrls.length) {
        throw new Error('No test images are available for this search.');
      }
      const result = await findSimilarCigars(imageUrls);
      const hydrated = await hydrateSimilarityCandidates(
        result.candidates || []
      );
      setSimilarityResult(result);
      setSimilarityCandidates(hydrated);
      setSimilaritySourceImage(
        showSourceImage ? getProxiedImageUrl(imageUrls[0]) : null
      );
      setShowSimilarityModal(true);
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to find similar cigars"
          message={getApiErrorMessage(
            err,
            'There was a problem running the similarity search.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      similarityRequests.current.delete(requestKey);
      setSimilarityPending(requestKey, false);
    }
  };

  const findSimilar = (cigar) => {
    const imageUrls = getMatcherTestImages(cigar.references.tests);
    if (!imageUrls.length) return;

    runSimilaritySearch({
      requestKey: getSimilarityRequestKey(imageUrls),
      resolveImageUrls: async () => imageUrls,
    });
  };

  const findSimilarImage = (imageUrl) => {
    runSimilaritySearch({
      requestKey: getSimilarityRequestKey([imageUrl]),
      resolveImageUrls: async () => {
        const fresh = await getFreshSelectedImage(imageUrl);
        return [fresh.image];
      },
      showSourceImage: true,
    });
  };

  const setIdentifyPending = (requestKey, pending) => {
    setIdentifyImageKeys((current) => {
      const next = new Set(current);
      if (pending) next.add(requestKey);
      else next.delete(requestKey);
      return next;
    });
  };

  const runIdentification = async ({
    requestKey,
    resolveImageUrls,
    showSourceImage = false,
  }) => {
    if (!requestKey || identifyRequests.current.has(requestKey)) return;

    try {
      identifyRequests.current.add(requestKey);
      setIdentifyPending(requestKey, true);
      const imageUrls = await resolveImageUrls();
      if (!imageUrls.length) {
        throw new Error('No test images are available for identification.');
      }

      const result = await identifyCigar(imageUrls, { suggestionLimit: 5 });
      const candidates: Array<
        IdentificationCandidate & { resultType: 'match' | 'suggestion' }
      > = [];
      if (result.match) {
        candidates.push({ ...result.match, resultType: 'match' });
      }
      for (const suggestion of result.suggestions || []) {
        if (suggestion.cigar_id !== result.match?.cigar_id) {
          candidates.push({ ...suggestion, resultType: 'suggestion' });
        }
      }

      const hydrated = await hydrateCigarCandidates(candidates, 10);
      setIdentifyResult(result);
      setIdentifyCandidates(hydrated);
      setIdentifySourceImage(
        showSourceImage ? getProxiedImageUrl(imageUrls[0]) : null
      );
      setShowIdentifyModal(true);
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to identify cigar"
          message={getApiErrorMessage(
            err,
            'There was a problem running the production identification flow.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      identifyRequests.current.delete(requestKey);
      setIdentifyPending(requestKey, false);
    }
  };

  const identifyImages = (cigar) => {
    const imageUrls = getMatcherTestImages(cigar.references.tests);
    if (!imageUrls.length) return;

    runIdentification({
      requestKey: getSimilarityRequestKey(imageUrls),
      resolveImageUrls: async () => imageUrls,
    });
  };

  const identifyImage = (imageUrl) => {
    runIdentification({
      requestKey: getSimilarityRequestKey([imageUrl]),
      resolveImageUrls: async () => {
        const fresh = await getFreshSelectedImage(imageUrl);
        return [fresh.image];
      },
      showSourceImage: true,
    });
  };

  const openMatchFlow = (image: string, sourceId?: string | number) => {
    setMatchFlowSource({
      image,
      sourceType: 'recognition_test',
      sourceId,
    });
  };

  const openEvaluation = () => {
    setEvaluationCigarIds(
      filteredCigars
        .slice(0, 10)
        .map((cigar) => cigar.id)
        .join(', ')
    );
    setEvaluationResult(null);
    setShowEvaluationModal(true);
  };

  const runEvaluation = async () => {
    if (isEvaluating) return;
    const cigarIds = Array.from(
      new Set(
        evaluationCigarIds
          .split(/[\s,]+/)
          .map(Number)
          .filter((cigarId) => Number.isInteger(cigarId) && cigarId > 0)
      )
    );

    if (!cigarIds.length) {
      Toast.error(
        <ToastContent
          title="Cigar IDs required"
          message="Enter at least one positive cigar ID to evaluate."
        />,
        { position: 'top-right' }
      );
      return;
    }

    try {
      setIsEvaluating(true);
      setEvaluationResult(null);
      const result = await evaluateCigarMatcher({
        cigarIds,
        maximumImages: Math.min(25, Math.max(1, evaluationMaximumImages || 25)),
        maximumPerCigar: Math.min(
          25,
          Math.max(1, evaluationMaximumPerCigar || 5)
        ),
        includeDetails: true,
      });
      setEvaluationResult(result);
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to evaluate matcher"
          message={getApiErrorMessage(
            err,
            'There was a problem evaluating the held-out test images.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      setIsEvaluating(false);
    }
  };

  const confirmRegeneration = (cigar) => {
    ModalDialog.show({
      title: 'Regenerate embeddings?',
      message:
        'This is slower and will upsert embeddings for the current model and version.',
      type: 'warning',
      buttons: [
        { label: 'Cancel', onClick: () => ModalDialog.close() },
        {
          label: 'Regenerate',
          color: 'warning',
          onClick: () => {
            ModalDialog.close();
            generateEmbeddings(cigar, true);
          },
        },
      ],
    });
  };

  const confirmFingerprintRegeneration = (cigar) => {
    ModalDialog.show({
      title: 'Regenerate fingerprint?',
      message:
        'This will replace existing fingerprints generated from up to three reference images for this cigar.',
      type: 'warning',
      buttons: [
        { label: 'Cancel', onClick: () => ModalDialog.close() },
        {
          label: 'Regenerate',
          color: 'warning',
          onClick: () => {
            ModalDialog.close();
            generateFingerprint(cigar, true);
          },
        },
      ],
    });
  };

  const renderAiActions = (cigar) => {
    const embeddingPending = embeddingCigarIds.has(String(cigar.id));
    const fingerprintPending = fingerprintCigarIds.has(String(cigar.id));
    const matcherImages = getMatcherTestImages(cigar.references.tests);
    const similarityPending = similarityImageKeys.has(
      getSimilarityRequestKey(matcherImages)
    );
    const identifyPending = identifyImageKeys.has(
      getSimilarityRequestKey(matcherImages)
    );
    const hasTestImages = Boolean(matcherImages.length);
    const actionAvailability = getCoverageTestingAvailability(
      coverageByCigarId[Number(cigar.id)],
      hasTestImages
    );
    let similarityActionTitle = 'Find similar';
    if (similarityPending) similarityActionTitle = 'Searching…';
    else if (actionAvailability.targetMissingEmbeddings) {
      similarityActionTitle = 'Find similar — target has no embeddings';
    }

    return (
      <div className="recognition-ai-actions">
        {similarityPending && (
          <span className="recognition-ai-action-progress">
            <Icon name="loader" className="recognition-spinner" size={14} />
            Finding similar…
          </span>
        )}
        {identifyPending && (
          <span className="recognition-ai-action-progress">
            <Icon name="loader" className="recognition-spinner" size={14} />
            Identifying…
          </span>
        )}
        <RowMoreOptions
          items={[
            {
              type: 'MenuItem',
              title: embeddingPending
                ? 'Generating embeddings…'
                : 'Generate embeddings',
              onClick: () => {
                if (!embeddingPending) generateEmbeddings(cigar);
              },
              style: embeddingPending ? { opacity: 0.55 } : undefined,
            },
            {
              type: 'MenuItem',
              title: 'Regenerate embeddings',
              onClick: () => {
                if (!embeddingPending) confirmRegeneration(cigar);
              },
              style: embeddingPending ? { opacity: 0.55 } : undefined,
            },
            {
              type: 'MenuItem',
              title: fingerprintPending
                ? 'Generating fingerprint…'
                : 'Generate Fingerprint',
              onClick: () => {
                if (!fingerprintPending) generateFingerprint(cigar);
              },
              style: fingerprintPending ? { opacity: 0.55 } : undefined,
            },
            {
              type: 'MenuItem',
              title: 'Regenerate Fingerprint',
              onClick: () => {
                if (!fingerprintPending) confirmFingerprintRegeneration(cigar);
              },
              style: fingerprintPending ? { opacity: 0.55 } : undefined,
            },
            { type: 'Divider' },
            {
              type: 'MenuItem',
              title: similarityActionTitle,
              onClick: () => {
                if (!similarityPending && actionAvailability.canFindSimilar) {
                  findSimilar(cigar);
                }
              },
              style:
                similarityPending || !actionAvailability.canFindSimilar
                  ? { opacity: 0.55 }
                  : undefined,
            },
            {
              type: 'MenuItem',
              title: identifyPending ? 'Identifying…' : 'Identify',
              onClick: () => {
                if (!identifyPending && actionAvailability.canIdentify) {
                  identifyImages(cigar);
                }
              },
              style:
                identifyPending || !actionAvailability.canIdentify
                  ? { opacity: 0.55 }
                  : undefined,
            },
            {
              type: 'MenuItem',
              title: 'Test Match',
              onClick: () => {
                if (matcherImages[0]) {
                  openMatchFlow(matcherImages[0], cigar.id);
                }
              },
              style: !hasTestImages ? { opacity: 0.55 } : undefined,
            },
          ]}
        />
      </div>
    );
  };

  const getSimilarityModalTitle = () => {
    if (similaritySourceImage) {
      return similarityResult?.matched
        ? 'Single-image similarity match'
        : 'Single-image suggestions';
    }

    return similarityResult?.matched
      ? 'Confirmed similarity match'
      : 'Similarity suggestions';
  };

  const renderSimilarityModal = () => (
    <Modal
      isOpen={showSimilarityModal}
      toggle={() => {
        setShowSimilarityModal(false);
        setSimilaritySourceImage(null);
      }}
      className="modal-dialog-lg"
    >
      <div className="modal-header">
        <h5 className="modal-title">{getSimilarityModalTitle()}</h5>
      </div>
      <div className="modal-body">
        {similaritySourceImage && (
          <div className="recognition-similarity-source">
            <img src={similaritySourceImage} alt="Test image evaluated" />
            <div>
              <strong>Test image evaluated</strong>
              <span>
                These results use only this image, matching the main app's
                single-image recognition flow.
              </span>
            </div>
          </div>
        )}
        <div
          className={`recognition-result-summary ${
            similarityResult?.matched ? 'matched' : 'suggestions'
          }`}
        >
          <div>
            <strong>
              {similarityResult?.matched
                ? 'Embedding match accepted'
                : 'Embedding suggestions only'}
            </strong>
            <span>
              {formatDecisionReason(similarityResult?.decisionReason)}
            </span>
          </div>
          {similarityResult?.decision_margin !== undefined && (
            <div className="recognition-result-metric">
              <small>Decision margin</small>
              <strong>{formatScore(similarityResult.decision_margin)}</strong>
            </div>
          )}
        </div>
        {!similarityCandidates.length ? (
          <p>No similar embedded reference images were found.</p>
        ) : (
          <Table responsive className="recognition-results-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Cigar</th>
                <th>Visual similarity</th>
                <th title="Distinct source photographs supporting this candidate.">
                  Source photos
                </th>
                <th title="Diagnostic count of matching deterministic embedding views.">
                  Matched views
                </th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {similarityCandidates.map((candidate, index) => {
                const confirmed = Boolean(
                  similarityResult?.matched && index === 0
                );
                return (
                  <tr
                    key={`${candidate.cigar_id}-${index}`}
                    className={
                      confirmed ? 'similarity-confirmed-match' : undefined
                    }
                  >
                    <td>{index + 1}</td>
                    <td>
                      {candidate.cigarName || 'Unknown cigar'}
                      <small className="d-block text-muted">
                        ID {candidate.cigar_id}
                      </small>
                    </td>
                    <td>
                      <strong>{formatScore(candidate.similarity)}</strong>
                      <small className="d-block text-muted">
                        Best {formatScore(candidate.best_similarity)} · Mean{' '}
                        {formatScore(candidate.mean_reference_similarity)}
                      </small>
                    </td>
                    <td>{candidate.supporting_references ?? '—'}</td>
                    <td>{candidate.matched_views ?? '—'}</td>
                    <td>{confirmed ? 'Automatic match' : 'Suggestion'}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {similarityResult?.metadata && (
          <div className="recognition-model-metadata">
            <span>
              Model{' '}
              <strong>{similarityResult.metadata.model || 'Unknown'}</strong>
            </span>
            <span>
              Version{' '}
              <strong>{similarityResult.metadata.version || 'Unknown'}</strong>
            </span>
            <span>
              {similarityResult.metadata.image_count ?? '—'} images ·{' '}
              {similarityResult.metadata.embedding_view_count ?? '—'} query
              views
            </span>
            {similarityResult.metadata.thresholds && (
              <span>
                Match ≥{' '}
                {formatScore(
                  similarityResult.metadata.thresholds.match_similarity
                )}{' '}
                · margin ≥{' '}
                {formatScore(similarityResult.metadata.thresholds.match_margin)}
                {' · '}
                {similarityResult.metadata.thresholds
                  .minimum_supporting_references ?? '—'}{' '}
                source photos
              </span>
            )}
          </div>
        )}
      </div>
      <div className="modal-footer">
        <Button
          onClick={() => {
            setShowSimilarityModal(false);
            setSimilaritySourceImage(null);
          }}
        >
          Close
        </Button>
      </div>
    </Modal>
  );

  const closeIdentifyModal = () => {
    setShowIdentifyModal(false);
    setIdentifySourceImage(null);
  };

  const renderIdentifyModal = () => {
    const status = identifyResult?.status || 'unmatched';
    const metadata = identifyResult?.metadata;
    const statusTitle = {
      matched: 'Cigar identified',
      suggestions: 'Identification suggestions',
      unmatched: 'No cigar identified',
    }[status];

    return (
      <Modal
        isOpen={showIdentifyModal}
        toggle={closeIdentifyModal}
        className="modal-dialog-lg"
      >
        <div className="modal-header">
          <div>
            <h5 className="modal-title">{statusTitle || 'Identify result'}</h5>
            <small className="text-muted">
              Complete production identification pipeline
            </small>
          </div>
        </div>
        <div className="modal-body">
          {identifySourceImage && (
            <div className="recognition-similarity-source">
              <img src={identifySourceImage} alt="Test image identified" />
              <div>
                <strong>Test image identified</strong>
                <span>
                  This result uses only this image through the full production
                  pipeline.
                </span>
              </div>
            </div>
          )}

          <div className={`recognition-result-summary ${status}`}>
            <div>
              <strong>{statusTitle || status}</strong>
              <span>{formatDecisionReason(metadata?.decision_reason)}</span>
            </div>
            <div className="recognition-pipeline-flags">
              <span className={metadata?.embedding_resolved ? 'active' : ''}>
                Embeddings {metadata?.embedding_resolved ? 'resolved' : 'open'}
              </span>
              <span className={metadata?.gpt_fallback_used ? 'active' : ''}>
                GPT fallback {metadata?.gpt_fallback_used ? 'used' : 'not used'}
              </span>
            </div>
          </div>

          {identifyCandidates.length ? (
            <div className="recognition-candidate-grid">
              {identifyCandidates.map((candidate, index) => (
                <article
                  className={`recognition-candidate-card ${candidate.resultType}`}
                  key={`${candidate.cigar_id}-${candidate.resultType}-${index}`}
                >
                  <div className="recognition-candidate-image">
                    {candidate.cigarImageUrl ? (
                      <img
                        src={candidate.cigarImageUrl}
                        alt={candidate.cigarName || 'Cigar band'}
                      />
                    ) : (
                      <Icon name="image" size={24} />
                    )}
                  </div>
                  <div className="recognition-candidate-content">
                    <small>
                      {candidate.resultType === 'match'
                        ? 'Production match'
                        : 'Suggestion'}
                    </small>
                    <strong>{candidate.cigarName || 'Unknown cigar'}</strong>
                    <a
                      href={`/cigars/manager?id=${candidate.cigar_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ID {candidate.cigar_id}
                    </a>
                    <div className="recognition-candidate-metrics">
                      <span>
                        Confidence {formatPercentage(candidate.confidence)}
                      </span>
                      {candidate.similarity !== undefined && (
                        <span>
                          Visual similarity {formatScore(candidate.similarity)}
                        </span>
                      )}
                      {candidate.supporting_references !== undefined && (
                        <span>
                          {candidate.supporting_references} source photos
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="recognition-identify-empty">
              <Icon name="help-circle" size={28} />
              <strong>No catalog candidate was returned.</strong>
              <span>The server safely left this request unmatched.</span>
            </div>
          )}

          {metadata && (
            <div className="recognition-model-metadata">
              <span>
                Source <strong>{formatDecisionReason(metadata.source)}</strong>
              </span>
              <span>
                {metadata.processing_ms ?? '—'} ms ·{' '}
                {metadata.image_count ?? '—'} images ·{' '}
                {metadata.candidate_count ?? '—'} candidates
              </span>
              {metadata.embedding_model && (
                <span>
                  Embedding model <strong>{metadata.embedding_model}</strong>
                </span>
              )}
              {metadata.embedding_version && (
                <span>
                  Version <strong>{metadata.embedding_version}</strong>
                </span>
              )}
              {metadata.multimodal_model && (
                <span>
                  Multimodal <strong>{metadata.multimodal_model}</strong>
                </span>
              )}
              {metadata.token_usage?.total_tokens !== undefined && (
                <span>{metadata.token_usage.total_tokens} tokens</span>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button onClick={closeIdentifyModal}>Close</Button>
        </div>
      </Modal>
    );
  };

  const renderEvaluationModal = () => (
    <Modal
      isOpen={showEvaluationModal}
      toggle={() => {
        if (!isEvaluating) setShowEvaluationModal(false);
      }}
      className="modal-dialog-lg recognition-evaluation-modal"
    >
      <div className="modal-header">
        <div>
          <h5 className="modal-title">Evaluate V2 matcher</h5>
          <small className="text-muted">
            Uses held-out test images only; no reference rows are created.
          </small>
          <small className="d-block text-muted">
            Evaluation does not return model/version; confirm it in Find Similar
            before comparing runs.
          </small>
        </div>
      </div>
      <div className="modal-body">
        <div className="recognition-evaluation-form">
          <label htmlFor="evaluation-cigar-ids">
            Cigar IDs
            <textarea
              id="evaluation-cigar-ids"
              className="form-control"
              rows={2}
              disabled={isEvaluating}
              value={evaluationCigarIds}
              onChange={(event) => setEvaluationCigarIds(event.target.value)}
              placeholder="123, 456, 789"
            />
          </label>
          <label htmlFor="evaluation-maximum-images">
            Maximum images
            <input
              id="evaluation-maximum-images"
              className="form-control"
              type="number"
              min={1}
              max={25}
              disabled={isEvaluating}
              value={evaluationMaximumImages}
              onChange={(event) =>
                setEvaluationMaximumImages(Number(event.target.value))
              }
            />
          </label>
          <label htmlFor="evaluation-maximum-per-cigar">
            Per cigar
            <input
              id="evaluation-maximum-per-cigar"
              className="form-control"
              type="number"
              min={1}
              max={25}
              disabled={isEvaluating}
              value={evaluationMaximumPerCigar}
              onChange={(event) =>
                setEvaluationMaximumPerCigar(Number(event.target.value))
              }
            />
          </label>
          <button
            type="button"
            className="btn btn-primary"
            disabled={isEvaluating || !evaluationCigarIds.trim()}
            onClick={runEvaluation}
          >
            {isEvaluating ? 'Evaluating…' : 'Run evaluation'}
          </button>
        </div>

        {evaluationResult && (
          <div className="recognition-evaluation-results">
            <div className="recognition-evaluation-summary">
              <div>
                <small>Top-1 accuracy</small>
                <strong>
                  {formatPercentage(evaluationResult.accuracy?.top_1)}
                </strong>
              </div>
              <div>
                <small>Top-3 accuracy</small>
                <strong>
                  {formatPercentage(evaluationResult.accuracy?.top_3)}
                </strong>
              </div>
              <div>
                <small>Top-5 accuracy</small>
                <strong>
                  {formatPercentage(evaluationResult.accuracy?.top_5)}
                </strong>
              </div>
              <div>
                <small>Automation coverage</small>
                <strong>
                  {formatPercentage(evaluationResult.automation?.coverage)}
                </strong>
              </div>
              <div>
                <small>False positives</small>
                <strong>
                  {evaluationResult.automation?.false_positive_count ?? '—'} ·{' '}
                  {formatPercentage(
                    evaluationResult.automation?.false_positive_rate
                  )}
                </strong>
              </div>
              <div>
                <small>Dataset</small>
                <strong>
                  {evaluationResult.image_count ?? '—'} images ·{' '}
                  {evaluationResult.cigar_count ?? '—'} cigars
                </strong>
              </div>
            </div>

            <h6>Score distributions</h6>
            <Table responsive size="sm" className="recognition-results-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Minimum</th>
                  <th>Average</th>
                  <th>P50</th>
                  <th>P95</th>
                  <th>Maximum</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Decision score', evaluationResult.confidence],
                  ['Visual similarity', evaluationResult.similarity],
                  ['Margin', evaluationResult.margin],
                  ['Latency (ms)', evaluationResult.latency_ms],
                ].map(([label, distribution]) => (
                  <tr key={String(label)}>
                    <td>{label}</td>
                    <td>{formatScore(distribution?.minimum)}</td>
                    <td>{formatScore(distribution?.average)}</td>
                    <td>{formatScore(distribution?.p50)}</td>
                    <td>{formatScore(distribution?.p95)}</td>
                    <td>{formatScore(distribution?.maximum)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {Boolean(evaluationResult.details?.length) && (
              <>
                <h6>Per-image diagnostics</h6>
                <Table
                  responsive
                  size="sm"
                  className="recognition-results-table"
                >
                  <thead>
                    <tr>
                      <th>Test image</th>
                      <th>Expected</th>
                      <th>Ranked cigar IDs</th>
                      <th>Visual similarity</th>
                      <th>Runner-up</th>
                      <th>Margin</th>
                      <th>Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evaluationResult.details?.map((detail) => (
                      <tr key={detail.source_image_id}>
                        <td title={detail.source_image_id}>
                          {getImageKey(detail.source_image_id)}
                        </td>
                        <td>{detail.expected_cigar_id}</td>
                        <td>{detail.predicted_cigar_ids?.join(', ') || '—'}</td>
                        <td>{formatScore(detail.similarity)}</td>
                        <td>{formatScore(detail.runner_up_similarity)}</td>
                        <td>{formatScore(detail.margin)}</td>
                        <td>
                          {detail.auto_matched ? 'Auto-match' : 'Review'}
                          <small className="d-block text-muted">
                            {formatDecisionReason(detail.decision_reason)}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </div>
        )}
      </div>
      <div className="modal-footer">
        <Button
          disabled={isEvaluating}
          onClick={() => setShowEvaluationModal(false)}
        >
          Close
        </Button>
      </div>
    </Modal>
  );

  const closeImagesModal = () => {
    if (urlImportRequest.current || cropSaveRequest.current) return;
    setShowModal(false);
  };

  const renderModal = () => (
    <Modal
      isOpen={showModal}
      onClosed={() => setShowModal(false)}
      toggle={closeImagesModal}
      className="modal-dialog-lg recognition-images-modal"
    >
      <div className="modal-header recognition-modal-header">
        <div>
          <h5 className="modal-title" id="cigar-modal-title">
            {selectedType === 'tests' ? 'Test Images' : 'Training Images'}
          </h5>
          <p>
            Cigar {selectedCigarId} · {selectedImages.length}{' '}
            {selectedImages.length === 1 ? 'image' : 'images'}
          </p>
        </div>
        <button
          type="button"
          className="recognition-modal-close"
          aria-label="Close"
          disabled={isPreparingUrlImage || isSavingCroppedImage}
          onClick={closeImagesModal}
        >
          ×
        </button>
      </div>

      <div className="modal-body recognition-modal-body">
        <section className="recognition-modal-section">
          <div className="recognition-modal-section-heading">
            <div>
              <h6>Image gallery</h6>
              <span>Hover an image to preview, crop, remove, or test it.</span>
            </div>
          </div>

          {selectedImages.length ? (
            <div className="recognition-modal-gallery">
              {selectedImages.slice(0, 20).map((image, index) => (
                <div className="recognition-modal-image" key={image}>
                  <img src={image} alt={`Cigar image ${index + 1}`} />
                  {selectedType === 'tests' &&
                    (similarityImageKeys.has(
                      getSimilarityRequestKey([image])
                    ) ||
                      identifyImageKeys.has(
                        getSimilarityRequestKey([image])
                      )) && (
                      <div className="recognition-modal-image-progress">
                        <Icon name="loader" size={14} />
                        <span>
                          {identifyImageKeys.has(
                            getSimilarityRequestKey([image])
                          )
                            ? 'Identifying…'
                            : 'Finding similar…'}
                        </span>
                      </div>
                    )}
                  <div className="recognition-modal-image-actions">
                    <div className="recognition-modal-image-actions-top">
                      <button
                        type="button"
                        title={
                          preparingImageKey === getImageKey(image)
                            ? 'Loading image…'
                            : 'View full size'
                        }
                        disabled={Boolean(preparingImageKey)}
                        onClick={() => maximizeImage(image)}
                      >
                        <Icon name="maximize2" size={17} />
                      </button>
                      <button
                        type="button"
                        title={
                          preparingImageKey === getImageKey(image)
                            ? 'Loading image…'
                            : 'Crop and replace image'
                        }
                        disabled={Boolean(preparingImageKey)}
                        onClick={() => editImage(image)}
                      >
                        <Icon name="edit2" size={17} />
                      </button>
                      <button
                        type="button"
                        className="delete"
                        title="Delete image"
                        onClick={() => {
                          ModalDialog.show({
                            title: 'Delete image?',
                            message:
                              'This image will be removed from the cigar image library.',
                            type: 'error',
                            buttons: [
                              {
                                label: 'Cancel',
                                onClick: () => ModalDialog.close(),
                              },
                              {
                                label: 'Delete',
                                style: { color: 'rgb(239, 81, 100)' },
                                onClick: () => deleteImage(image),
                              },
                            ],
                          });
                        }}
                      >
                        <Icon name="trash" size={17} />
                      </button>
                    </div>
                    {selectedType === 'tests' && (
                      <div className="recognition-modal-image-actions-bottom">
                        <button
                          type="button"
                          title="Open the stepped match debugger"
                          disabled={Boolean(preparingImageKey)}
                          onClick={() =>
                            openMatchFlow(
                              image,
                              `${selectedCigarId}:${getImageKey(image)}`
                            )
                          }
                        >
                          <Icon name="play-circle" size={17} />
                        </button>
                        <button
                          type="button"
                          title={
                            similarityImageKeys.has(
                              getSimilarityRequestKey([image])
                            )
                              ? 'Searching…'
                              : 'Find similar cigars using this image'
                          }
                          disabled={
                            Boolean(preparingImageKey) ||
                            similarityImageKeys.has(
                              getSimilarityRequestKey([image])
                            )
                          }
                          onClick={() => findSimilarImage(image)}
                        >
                          <Icon
                            name={
                              similarityImageKeys.has(
                                getSimilarityRequestKey([image])
                              )
                                ? 'loader'
                                : 'search'
                            }
                            className={
                              similarityImageKeys.has(
                                getSimilarityRequestKey([image])
                              )
                                ? 'recognition-spinner'
                                : undefined
                            }
                            size={17}
                          />
                        </button>
                        <button
                          type="button"
                          title={
                            identifyImageKeys.has(
                              getSimilarityRequestKey([image])
                            )
                              ? 'Identifying…'
                              : 'Identify using the production pipeline'
                          }
                          disabled={
                            Boolean(preparingImageKey) ||
                            identifyImageKeys.has(
                              getSimilarityRequestKey([image])
                            )
                          }
                          onClick={() => identifyImage(image)}
                        >
                          <Icon
                            name={
                              identifyImageKeys.has(
                                getSimilarityRequestKey([image])
                              )
                                ? 'loader'
                                : 'crosshair'
                            }
                            className={
                              identifyImageKeys.has(
                                getSimilarityRequestKey([image])
                              )
                                ? 'recognition-spinner'
                                : undefined
                            }
                            size={17}
                          />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="recognition-modal-empty">
              <Icon name="image" size={28} />
              <strong>No images yet</strong>
              <span>Add the first image below.</span>
            </div>
          )}
        </section>

        <section className="recognition-modal-section recognition-user-images">
          <div className="recognition-modal-section-heading">
            <div>
              <h6>User-uploaded images</h6>
              <span>
                Crop an image from the main app and import it as a new{' '}
                {selectedType === 'tests' ? 'test' : 'training'} image.
              </span>
            </div>
          </div>

          {isLoadingUserImages && (
            <div className="recognition-user-images-status">
              Loading user-uploaded images…
            </div>
          )}

          {!isLoadingUserImages && userImagesError && (
            <div className="recognition-user-images-status error">
              <span>{userImagesError}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  setUserImagesReloadToken((current) => current + 1)
                }
              >
                Try again
              </button>
            </div>
          )}

          {!isLoadingUserImages &&
            !userImagesError &&
            Boolean(userUploadedImages.length) && (
              <div className="recognition-user-images-gallery">
                {userUploadedImages.map((image, index) => (
                  <div className="recognition-user-image" key={image}>
                    <div className="recognition-user-image-preview">
                      <img
                        src={getProxiedImageUrl(image)}
                        alt={`User-uploaded cigar image ${index + 1}`}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={isSavingCroppedImage}
                      onClick={() => openCropper(image)}
                    >
                      Import and crop
                    </button>
                  </div>
                ))}
              </div>
            )}

          {!isLoadingUserImages &&
            !userImagesError &&
            !userUploadedImages.length && (
              <div className="recognition-user-images-status">
                No user-uploaded images were found for this cigar.
              </div>
            )}
        </section>

        <section className="recognition-modal-section recognition-add-images">
          <div className="recognition-modal-section-heading">
            <div>
              <h6>Add an image</h6>
              <span>Upload an image or import one from a public URL.</span>
            </div>
          </div>

          <Dropzone
            accept={{
              'image/jpeg': ['.jpg', '.jpeg'],
              'image/png': ['.png'],
            }}
            disabled={isSavingCroppedImage}
            multiple={false}
            onDrop={handleImageDrop}
            onDropRejected={() => {
              Toast.error(
                <ToastContent
                  title="Unsupported image"
                  message="Choose one JPG or PNG image."
                />,
                { position: 'top-right' }
              );
            }}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                id="add-images-dropzone"
                className={`recognition-dropzone${
                  isDragActive ? ' active' : ''
                }`}
                {...getRootProps()}
              >
                <input {...getInputProps()} />
                <Icon name="upload-cloud" size={24} />
                <strong>
                  {isDragActive
                    ? 'Drop the image to crop it'
                    : 'Drop an image here or click to browse'}
                </strong>
                <span>JPG or PNG</span>
              </div>
            )}
          </Dropzone>

          <div className="recognition-import-divider">
            <span>or</span>
          </div>

          <div className="recognition-url-import">
            <label htmlFor="recognition-image-url">Image URL</label>
            <div className="input-group">
              <input
                id="recognition-image-url"
                className="form-control"
                type="url"
                name="image-url"
                placeholder="https://example.com/cigar.jpg"
                value={urlToImport}
                onChange={(e) => setUrlToImport(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') cropImageFromUrl();
                }}
              />

              <div className="input-group-append">
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={
                    !urlToImport.trim() ||
                    isPreparingUrlImage ||
                    isSavingCroppedImage
                  }
                  onClick={cropImageFromUrl}
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                >
                  {isPreparingUrlImage ? 'Preparing…' : 'Crop image'}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="modal-footer recognition-modal-footer">
        <Button
          color="light"
          disabled={isPreparingUrlImage || isSavingCroppedImage}
          onClick={closeImagesModal}
        >
          Done
        </Button>
      </div>
    </Modal>
  );

  return (
    <div>
      <Breadcrumb
        title="Cigar Band Recognition"
        label="Band Recognition"
        parent="Cigar Manager"
      />

      <div className="container-fluid" style={{ marginTop: 20 }}>
        {canManageCatalogRecognition && (
          <>
            <BulkEmbeddingJobs
              onCoverageChanged={() =>
                setCoverageRefreshToken((current) => current + 1)
              }
            />
            <BulkFingerprintJobs />
          </>
        )}
        <Card className="recognition-manager-card">
          <CardHeader className="recognition-manager-header">
            <div>
              <h5>Cigar image library</h5>
              <p>
                Review training and test images, generate embeddings, and run
                similarity checks.
              </p>
            </div>
            <div className="recognition-stats">
              <strong>{imageCount.toLocaleString()}</strong>
              <span>reference images</span>
            </div>
          </CardHeader>

          <div className="recognition-toolbar">
            <div className="recognition-search">
              <Icon name="search" size={16} />
              <DebounceSearchBar
                value={searchText}
                placeholder="Search by cigar ID or name..."
                onSearch={(query) => {
                  setCurrentPage(1);
                  setSearchText(query);
                }}
              />
              {searchText && (
                <button
                  type="button"
                  className="recognition-search-clear"
                  aria-label="Clear search"
                  onClick={() => setSearchText('')}
                >
                  ×
                </button>
              )}
            </div>
            <div className="recognition-toolbar-actions">
              <span className="recognition-result-count">
                {filteredCigars.length} cigars on this page
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={!filteredCigars.length}
                onClick={openEvaluation}
              >
                <Icon name="bar-chart-2" size={14} />
                Evaluate
              </button>
              <UploadMatch onReady={setMatchFlowSource} />
            </div>
          </div>

          <div className="table-responsive">
            <Table className="recognition-table" hover>
              <thead>
                <tr>
                  <th scope="col">Cigar</th>
                  <th scope="col">Embeddings</th>
                  <th scope="col">Reference Images</th>
                  <th scope="col">Test Images</th>
                  <th scope="col" aria-label="AI actions" />
                </tr>
              </thead>

              <tbody>
                {filteredCigars.map((cigar) => (
                  <tr key={cigar.id}>
                    <td className="recognition-cigar-cell">
                      <div className="recognition-cigar-name">
                        {cigar.fullName || 'Name unavailable'}
                      </div>
                      <div className="recognition-cigar-meta">
                        <a
                          href={`/cigars/manager?id=${cigar.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          ID {cigar.id}
                        </a>
                        <button
                          type="button"
                          title="Search Google Images"
                          onClick={() => searchGoogle(cigar.id)}
                        >
                          <Icon name="search" size={13} />
                          Google Images
                        </button>
                      </div>
                    </td>

                    <td className="recognition-coverage-cell">
                      <EmbeddingCoverageCell
                        coverage={coverageByCigarId[Number(cigar.id)]}
                        model={coverageModel}
                        version={coverageVersion}
                        isLoading={isLoadingCoverage}
                        isUnavailable={isCoverageUnavailable}
                      />
                    </td>

                    <td>
                      {renderImageGrid(
                        cigar.id,
                        cigar.references.training,
                        'training'
                      )}
                    </td>

                    <td>
                      {renderImageGrid(
                        cigar.id,
                        cigar.references.tests,
                        'tests'
                      )}
                    </td>

                    <td className="recognition-actions-cell">
                      {renderAiActions(cigar)}
                    </td>
                  </tr>
                ))}
                {!isLoadingRecords && filteredCigars.length === 0 && (
                  <tr>
                    <td colSpan={5} className="recognition-empty-state">
                      <Icon name="search" size={24} />
                      <strong>No cigars match this filter</strong>
                      <span>Try a different cigar ID or name.</span>
                    </td>
                  </tr>
                )}
                {isLoadingRecords && (
                  <tr>
                    <td colSpan={5} className="recognition-empty-state">
                      <strong>Loading cigar images…</strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="recognition-pagination">
            <span className="recognition-pagination-total">
              Showing {totalCount ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to{' '}
              {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
            </span>
            <StandalonePagination
              manual
              paginationProps={{
                page: currentPage,
                sizePerPage: PAGE_SIZE,
                totalSize: totalCount,
                onPageChange: setCurrentPage,
              }}
            />
          </div>
        </Card>
      </div>

      {renderModal()}
      {renderSimilarityModal()}
      {renderIdentifyModal()}
      {renderEvaluationModal()}
      <MatchFlowModal
        isOpen={Boolean(matchFlowSource)}
        source={matchFlowSource}
        onClose={() => setMatchFlowSource(null)}
        onAccept={() => {
          // TODO: Use accepted matches to improve recognition training data.
          Toast.info(
            <ToastContent
              title="Coming soon"
              message="Applying accepted matches to recognition training is not available yet."
            />,
            { position: 'top-right' }
          );
        }}
      />

      <MediaCropper
        src={cropSource}
        open={showMediaCropper}
        toggle={closeCropper}
        onClose={closeCropper}
        onSave={saveCroppedImage}
        saving={isSavingCroppedImage}
      />

      {showLightbox && lightboxImages.length > 0 && (
        <Lightbox
          mainSrc={lightboxImages[selectedIndex]}
          nextSrc={lightboxImages[(selectedIndex + 1) % lightboxImages.length]}
          prevSrc={
            lightboxImages[
              (selectedIndex + lightboxImages.length - 1) %
                lightboxImages.length
            ]
          }
          imageTitle={`${selectedIndex + 1}/${lightboxImages.length}`}
          onCloseRequest={() => {
            setShowLightbox(false);
            setLightboxImages([]);
          }}
          onMovePrevRequest={() =>
            setSelectedIndex(
              (selectedIndex + lightboxImages.length - 1) %
                lightboxImages.length
            )
          }
          onMoveNextRequest={() =>
            setSelectedIndex((selectedIndex + 1) % lightboxImages.length)
          }
        />
      )}
    </div>
  );
}

export default CigarBandRecognition;
